// src/utils/templeOrder.js
// Helper สำหรับ "ลำดับที่จัดเอง (ลากวาง)" แบบแยกตามขอบเขตสิทธิ์ (ภาค/จังหวัด/อำเภอ/ตำบล)
//
// กติกา (Effective Order):
//   1. ขอบเขตที่กำลังดูมีลำดับของตัวเอง           -> ใช้ลำดับนั้นทันที
//   2. ไม่มีลำดับของตัวเอง                       -> เรียงตามมาตรฐาน แต่ภายในกลุ่มย่อย
//      (จังหวัด/อำเภอ/ตำบล) ที่มีลำดับของตัวเอง   -> ใช้ลำดับนั้นภายในกลุ่ม (cascade)
//   3. ยังไม่มีใครจัดลำดับเลย                     -> เทียบเท่าลำดับมาตรฐาน (ไม่เปลี่ยนจากเดิม)
//   4. วัดที่เพิ่งเพิ่ม (ไม่อยู่ในลำดับที่บันทึกไว้) -> ต่อท้ายด้วยลำดับมาตรฐาน

export const SCOPE_TYPES = ['ภาค', 'จังหวัด', 'อำเภอ', 'ตำบล'];

// ลำดับหมวดประเภทวัด (พระอารามหลวง -> วิสุงคามสีมา -> พัทธสีมา -> วัดราษฎร์ -> ที่พักสงฆ์)
export const TYPE_ORDER = {
  'พระอารามหลวง': 1,
  'วิสุงคามสีมา': 2,
  'พัทธสีมา': 3,
  'วัดราษฎร์': 4,
  'ที่พักสงฆ์': 5,
};

const thaiCollator = new Intl.Collator('th', { sensitivity: 'base' });

// ลำดับมาตรฐาน: ประเภทวัด -> จังหวัด -> อำเภอ -> ตำบล -> ชื่อวัด
export function standardSort(list) {
  return [...list].sort((a, b) => {
    const typeDiff = (TYPE_ORDER[a.type] || 99) - (TYPE_ORDER[b.type] || 99);
    if (typeDiff !== 0) return typeDiff;
    for (const key of ['province', 'district', 'subdistrict', 'name']) {
      const diff = thaiCollator.compare(a[key] || '', b[key] || '');
      if (diff !== 0) return diff;
    }
    return (a.id || 0) - (b.id || 0);
  });
}

// key ของขอบเขตจากข้อมูลผู้ใช้ (level + ที่อยู่ที่สังกัด)
export function getUserScopeKey(profile) {
  if (!profile) return 'root';
  const { level, province, district, subdistrict } = profile;
  if (level === 'ตำบล' && subdistrict) {
    return `subdistrict:${province}:${district}:${subdistrict}`;
  }
  if (level === 'อำเภอ' && district) {
    return `district:${province}:${district}`;
  }
  if (level === 'จังหวัด' && province) {
    return `province:${province}`;
  }
  return 'root';
}

// key ของขอบเขตจากส่วนประกอบ { type, province, district, subdistrict }
export function scopeKeyFromParts(scope) {
  if (!scope) return 'root';
  const type = scope.type || 'ภาค';
  if (type === 'ตำบล' && scope.subdistrict) {
    return `subdistrict:${scope.province}:${scope.district}:${scope.subdistrict}`;
  }
  if (type === 'อำเภอ' && scope.district) {
    return `district:${scope.province}:${scope.district}`;
  }
  if (type === 'จังหวัด' && scope.province) {
    return `province:${scope.province}`;
  }
  return 'root';
}

// แปลง rows จาก GET /api/temple-order เป็น Map<scopeKey, Map<templeId, ตำแหน่ง>>
export function buildOrderMap(rows = []) {
  const map = new Map();
  for (const r of rows) {
    const key = scopeKeyFromParts({
      type: r.scope_type,
      province: r.scope_province,
      district: r.scope_district,
      subdistrict: r.scope_subdistrict,
    });
    if (!map.has(key)) map.set(key, new Map());
    map.get(key).set(r.temple_id, r.sort_order);
  }
  return map;
}

// แยกส่วนประกอบของ scope key: 'province:X' | 'district:X:Y' | 'subdistrict:X:Y:Z' | 'root'
function parseScope(key) {
  const parts = String(key).split(':');
  if (parts[0] === 'root') return { type: 'root', a: '', b: '', c: '' };
  return { type: parts[0], a: parts[1] || '', b: parts[2] || '', c: parts[3] || '' };
}

// desc อยู่ใต้ parent หรือเท่ากับ parent หรือไม่ (ตามลำดับชั้น ภาค > จังหวัด > อำเภอ > ตำบล)
function isDescendantOrSelf(desc, parent) {
  if (desc === parent) return true;
  const p = parseScope(parent);
  const d = parseScope(desc);
  if (p.type === 'root') return d.type !== 'root';
  if (p.type === 'province') {
    return (d.type === 'district' || d.type === 'subdistrict') && d.a === p.a;
  }
  if (p.type === 'district') {
    return d.type === 'subdistrict' && d.a === p.a && d.b === p.b;
  }
  return false; // subdistrict = กลุ่มย่อยสุดท้าย
}

// มีลำดับของ scope นี้หรือระดับย่อยลงไปไหม
function hasAnyOrderUnder(scopeKey, orderMap) {
  for (const key of orderMap.keys()) {
    if (isDescendantOrSelf(key, scopeKey)) return true;
  }
  return false;
}

// key ของระดับย่อยถัดไป (root -> จังหวัด -> อำเภอ -> ตำบล -> null = leaf)
function childKeyFor(temple, scopeKey) {
  if (scopeKey === 'root') return `province:${temple.province || ''}`;
  if (scopeKey.startsWith('province:')) {
    return `district:${temple.province || ''}:${temple.district || ''}`;
  }
  if (scopeKey.startsWith('district:')) {
    return `subdistrict:${temple.province || ''}:${temple.district || ''}:${temple.subdistrict || ''}`;
  }
  return null; // ระดับตำบล -> กลุ่มย่อยสุดท้าย
}

function scopeValue(key) {
  return key.split(':').slice(1).join(':');
}

// เรียงตามลำดับที่บันทึกไว้ของ scope (วัดที่ไม่อยู่ในลำดับ -> ต่อท้ายด้วยมาตรฐาน)
function sortByDirectOrder(list, directPosMap) {
  const withPos = [];
  const without = [];
  for (const t of list) {
    const pos = directPosMap.get(t.id);
    if (pos !== undefined) withPos.push({ t, pos });
    else without.push(t);
  }
  withPos.sort((a, b) => a.pos - b.pos || (a.t.id || 0) - (b.t.id || 0));
  return [...withPos.map((x) => x.t), ...standardSort(without)];
}

// ลำดับที่ใช้จริงสำหรับ list ในขอบเขต scopeKey
export function effectiveOrder(list, scopeKey, orderMap) {
  const direct = orderMap.get(scopeKey);
  if (direct) {
    return sortByDirectOrder(list, direct);
  }
  if (!hasAnyOrderUnder(scopeKey, orderMap)) {
    return standardSort(list); // ยังไม่มีใครจัด -> ลำดับมาตรฐานเหมือนเดิม
  }
  // cascade: แบ่งกลุ่มตามระดับย่อย เรียงกลุ่มด้วยชื่อ แล้ว recurse ลงแต่ละกลุ่ม
  const groups = new Map();
  const orphans = [];
  for (const t of list) {
    const childKey = childKeyFor(t, scopeKey);
    if (!childKey) {
      orphans.push(t);
      continue;
    }
    if (!groups.has(childKey)) groups.set(childKey, []);
    groups.get(childKey).push(t);
  }
  const childKeys = [...groups.keys()].sort((a, b) => thaiCollator.compare(scopeValue(a), scopeValue(b)));
  const result = [];
  for (const ck of childKeys) {
    result.push(...effectiveOrder(groups.get(ck), ck, orderMap));
  }
  result.push(...standardSort(orphans));
  return result;
}
